import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import history from "utils/history";
import {
  getAccessToken,
  getAdminAccessToken,
  getUserId,
  getUrlSuffix,
  getClientUserId,
  saveLoginToken,
  clearLoginToken
} from "utils/localStorageService";

const AuthContext = createContext(null);

const deriveStateFromStorage = () => {
  const access = !!getAccessToken();
  const adminAccess = !!getAdminAccessToken();
  const isTalent = access && (!!getUserId() || !!getUrlSuffix());
  const isClient = access && !!getClientUserId();
  const isAdmin = adminAccess;
  const isLoggedIn = isAdmin || isTalent || isClient;
  return { isLoggedIn, isAdmin, isTalent, isClient };
};

export const AuthProvider = ({ children }) => {
  const [authState, setAuthState] = useState(deriveStateFromStorage);

  useEffect(() => {
    const handleStorage = (e) => {
      if (!e.key) return;
      if (e.key.includes("access_token") || e.key.includes("admin_access_token") || e.key.includes("client_user_id") || e.key.includes("user_id") || e.key.includes("url_suffix")) {
        setAuthState(deriveStateFromStorage());
      }
    };
    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, []);

  const login = useCallback((response) => {
    saveLoginToken(response);
    setAuthState(deriveStateFromStorage());
    // Notify API modules that may schedule proactive refresh based on new JWTs.
    window.dispatchEvent(new Event("login"));
  }, []);

  const logout = useCallback(() => {
    const wasAdmin = authState.isAdmin;
    const wasClient = authState.isClient;
    clearLoginToken();
    setAuthState(deriveStateFromStorage());
    history.push(wasAdmin ? "/admin/login" : wasClient ? "/client/login" : "/login");
  }, [authState.isAdmin, authState.isClient]);

  const handleUnauthorized = useCallback(() => {
    const wasAdmin = authState.isAdmin;
    const wasClient = authState.isClient;
    clearLoginToken();
    setAuthState(deriveStateFromStorage());
    history.push(wasAdmin ? "/admin/login" : wasClient ? "/client/login" : "/login");
  }, [authState.isAdmin, authState.isClient]);

  const value = useMemo(() => ({
    ...authState,
    login,
    logout,
    handleUnauthorized
  }), [authState, login, logout, handleUnauthorized]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
};

